import { createContext, type ReactNode, useContext } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Profile, User, Viewer } from '@gobingebox/shared';
import { authApi } from './api';
import { profilesApi } from '@/features/profiles/api';

interface AuthContextValue {
  viewer: Viewer | undefined;
  user: User | null;
  isLoading: boolean;
  /** True when the API couldn't be reached (vs. simply logged out). */
  isApiDown: boolean;
  refresh: () => Promise<unknown>;
  profiles: Profile[];
  activeProfile: Profile | null;
  switchProfile: (id: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const VIEWER_QUERY_KEY = ['viewer'] as const;

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const query = useQuery<Viewer>({
    queryKey: VIEWER_QUERY_KEY,
    queryFn: authApi.me,
    staleTime: 60_000,
    retry: 1,
  });

  const profiles = query.data?.profiles ?? [];
  const activeProfileId = query.data?.activeProfileId ?? null;

  const value: AuthContextValue = {
    viewer: query.data,
    user: query.data?.user ?? null,
    isLoading: query.isLoading,
    isApiDown: query.isError,
    refresh: query.refetch,
    profiles,
    activeProfile: profiles.find((p) => p.id === activeProfileId) ?? null,
    switchProfile: async (id: string) => {
      await profilesApi.select(id);
      // Re-fetch viewer + per-profile data (list, continue watching, progress).
      await qc.invalidateQueries();
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}

/** Invalidate the cached viewer after login/logout/role changes. */
export function useRefreshViewer() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: VIEWER_QUERY_KEY });
}
